import { faLoader, faLongArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorSection } from '@pickleballinc/features/src/error-pages/error-section';
import Loader from '@pickleballinc/features/src/Loader/Loader';
import { Button, ProgressBar } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

import { useGetPlayerMatches } from '@/hooks/matches/hooks';
import { MatchCard } from '@/modules/results/components/match/MatchCard';
import { MatchPropsShort, MatchPropsShortModalRes, Player } from '@/modules/results/types';

interface PreviusMatchesModalTeamOneProps {
  isOpen: boolean;
  onClose: React.MouseEventHandler;
  calculateWinsTeam: number;
  calculateLostTeam: number;
  calculateMatchesNumber: string | number;
  teamOnePlayerOneName: string;
  teamOnePlayerTwoName: string;
  teamTwoPlayerOneName: string;
  teamTwoPlayerTwoName: string;
  isHistory: boolean;
  playerList: Player[];
  team: number;
  formatTitle: string;
  date: string;
  pbracketsURL?: string;
}

export function PreviusMatchesModalTeamOne({
  isOpen,
  onClose,
  calculateWinsTeam,
  calculateLostTeam,
  calculateMatchesNumber,
  isHistory,
  playerList,
  team,
  formatTitle,
  date,
  pbracketsURL,
}: PreviusMatchesModalTeamOneProps) {
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 767);

  let playerOneUuid = '';
  let playerTwoUuid = '';
  let matchType = '';
  if (playerList.length === 2) {
    matchType = 'singles';
    if (team === 1) {
      if (playerList[0]) {
        playerOneUuid = playerList[0].uuid;
      }
    } else {
      if (playerList[1]) {
        playerOneUuid = playerList[1].uuid;
      }
    }
  } else {
    matchType = 'doubles';
    if (team === 1) {
      if (playerList[0] && playerList[1]) {
        playerOneUuid = playerList[0].uuid;
        playerTwoUuid = playerList[1].uuid;
      }
    } else {
      if (playerList[2] && playerList[3]) {
        playerOneUuid = playerList[2].uuid;
        playerTwoUuid = playerList[3].uuid;
      }
    }
  }

  // getting match info data
  const {
    data: matchInfoData,
    isFetching,
    isLoading,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
  } = useGetPlayerMatches(playerOneUuid, playerTwoUuid, matchType, formatTitle, date);

  let errorMessage = null;
  let pages: MatchPropsShortModalRes[] = [];

  if (!matchInfoData || !matchInfoData.pages || matchInfoData.pages.length === 0) {
    errorMessage = 'No data found!';
  } else {
    const pageWithError = matchInfoData.pages.find((page) => page.error);
    if (pageWithError) {
      errorMessage = pageWithError.error;
    }
    pages = matchInfoData.pages;
  }

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }

    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isOpen, isFetching, hasNextPage]);

  const handleScroll = () => {
    if (scrollDivRef.current && !isLoading && !isFetching && hasNextPage) {
      const scrollableDiv = scrollDivRef.current;
      const bottom =
        Math.ceil((scrollableDiv.scrollHeight - scrollableDiv.clientHeight) / 100) * 100;
      const scroll = Math.ceil((scrollableDiv.clientHeight + scrollableDiv.scrollTop) / 100) * 100;
      if (scroll >= bottom) {
        fetchNextPage();
      }
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 767);
    };

    window.addEventListener('resize', handleResize);

    // Set initial state
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const scrollableDiv = scrollDivRef.current;
      if (scrollableDiv && !isLoading && !isFetching) {
        scrollableDiv.addEventListener('scroll', handleScroll);
      }

      return () => {
        if (scrollableDiv) {
          scrollableDiv.removeEventListener('scroll', handleScroll);
        }
      };
    }

    return () => {};
  }, [isOpen, isLoading, isFetching, hasNextPage]);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.1 } },
  };

  const contentVariants = {
    hidden: { y: '-50%', opacity: 0 },
    visible: {
      y: isDesktop ? '20%' : '25%',
      opacity: 1,
      transition: { duration: 0.1 },
    },
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial='hidden'
          animate='visible'
          exit='hidden'
          variants={backdropVariants}
          className='fixed inset-0 z-[60]'
          onClick={onClose}
          aria-labelledby='modal-title'
          role='dialog'
          aria-modal='true'
        >
          <div className='flex h-[80vh] items-end justify-center px-2 pb-20 pt-4 text-center sm:block sm:p-0'>
            <div className='fixed inset-0 bg-gray-500/75 transition-opacity' aria-hidden='true' />
            <span className='hidden sm:inline-block sm:h-screen sm:align-top' aria-hidden='true'>
              &#8203;
            </span>
            <motion.div
              initial='hidden'
              animate='visible'
              exit='hidden'
              variants={contentVariants}
              className='inline-block min-w-full overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all sm:min-w-[600px] sm:max-w-lg sm:align-middle'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='relative px-4 py-2 sm:px-6 sm:py-4'>
                <div className='sm:flex sm:items-start'>
                  <h3
                    className='w-full text-center text-2xl font-bold leading-6 text-gray-900'
                    id='modal-title'
                  >
                    History of matches
                  </h3>
                </div>
                <button
                  className=' absolute right-3 top-2 cursor-pointer rounded-md bg-gray-300 px-2 py-1 text-xs font-medium uppercase text-white'
                  onClick={onClose}
                >
                  x
                </button>
              </div>
              <div className='flex min-h-[90px]  border-b border-gray-300 pb-3'>
                <div className='flex flex-1 flex-col items-center justify-end'>
                  <div className='flex flex-col justify-center'>
                    <div className='mt-2 flex flex-col items-end'>
                      <span className='font-bold'>{calculateWinsTeam}</span>
                      {calculateMatchesNumber === 'No played matches' ? (
                        <span>{calculateMatchesNumber}</span>
                      ) : (
                        <span>{calculateMatchesNumber}% wins</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className='flex flex-1 flex-col items-center justify-center text-center'>
                  <div className='mb-4 text-xs sm:text-sm'>
                    <span>Matches played</span>
                  </div>
                  <ProgressBar progressColor='#59a65c' progress={Number(calculateMatchesNumber)} />
                </div>
                <div className='flex flex-1 flex-col items-center justify-end'>
                  <div className='flex flex-col justify-center'>
                    <div className='mt-2 flex flex-col'>
                      <span className='font-bold'>{calculateLostTeam}</span>
                      {calculateMatchesNumber === 'No played matches' ? (
                        <span>{calculateMatchesNumber}</span>
                      ) : (
                        <span>{(100 - Number(calculateMatchesNumber)).toFixed(2)}% wins</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                ref={scrollDivRef}
                className='grow overflow-y-auto bg-white px-2 pb-4 pt-1 sm:px-6 sm:pb-4'
                style={{ maxHeight: '60vh', minHeight: '60vh' }}
              >
                {isInitialLoading ? (
                  <Loader isComponent containerClassName='h-[56vh]' />
                ) : errorMessage ? (
                  <ErrorSection title={errorMessage} />
                ) : (
                  <div className='mt-2 flex flex-col gap-4'>
                    <ul className='flex flex-col gap-4'>
                      {pages
                        .flatMap((page) => page.results ?? [])
                        .map((match: MatchPropsShort) => (
                          <li key={match.match_uuid}>
                            <MatchCard
                              match={match}
                              isHistory={isHistory}
                              pbracketsURL={pbracketsURL}
                              playerMatchesArgs={{
                                playerOneUuid: playerOneUuid,
                                playerTwoUuid: playerTwoUuid,
                                matchType: matchType,
                                formatTitle: formatTitle,
                                date: date,
                              }}
                            />
                          </li>
                        ))}
                    </ul>

                    <div className='my-12 flex items-center justify-center'>
                      {hasNextPage ? (
                        <Button
                          variant='secondary'
                          size='md'
                          onClick={() => {
                            fetchNextPage();
                          }}
                          disabled={isFetching}
                          className={clsx(
                            'px-8',
                            isFetching ? 'bg-gray-100 text-gray-400 hover:bg-gray-200' : ''
                          )}
                        >
                          {isFetching ? 'Loading more...' : 'Load more'}
                          {isFetching ? (
                            <FontAwesomeIcon icon={faLoader} spin className='ml-2' />
                          ) : (
                            <FontAwesomeIcon icon={faLongArrowRight} className='ml-2' />
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
